import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AddIcon from "@mui/icons-material/Add";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axios from "axios";
import { MascaraCnpj } from "../../../utils/MascaraCnpj";
import { MascaraValor } from "../../../utils/MascaraValor";
import { TirarMascara } from "../../../utils/TirarMascara";
import { ENDERECO_VAZIO, enderecoCompleto, montarEndereco, separarEndereco } from "../../../utils/Endereco";
import CamposEndereco from "./CamposEndereco";
import {
    botaoPrincipalSx, botaoSecundarioSx, cardSx, gradeSx, iconeSecaoSx, inputSx, labelSx, subtituloSecaoSx, tituloSecaoSx,
} from "./EstilosFicha";

// "10000.00" (banco) -> "1.000.000" dígitos -> "10.000,00" (tela)
const capitalParaTela = (valor) => (valor ? MascaraValor(Number(valor).toFixed(2)) : "");
const capitalParaBanco = (texto) => (TirarMascara(texto) ? (Number(TirarMascara(texto)) / 100).toFixed(2) : null);

const SecaoTitulo = ({ icone, titulo, subtitulo }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={iconeSecaoSx}>{icone}</Box>
        <Box>
            <Typography sx={tituloSecaoSx}>{titulo}</Typography>
            {subtitulo && <Typography sx={subtituloSecaoSx}>{subtitulo}</Typography>}
        </Box>
    </Box>
);

// Etapa 1 da ficha: cria a ficha (dados da empresa) ou atualiza a existente
const DadosEmpresaFicha = ({ ficha, onSalvo, onErro, onAvancar }) => {
    const [cnpj, setCnpj] = useState("");
    const [razaoSocial, setRazaoSocial] = useState("");
    const [nomeFantasia, setNomeFantasia] = useState("");
    const [capitalSocial, setCapitalSocial] = useState("");
    const [endereco, setEndereco] = useState({ ...ENDERECO_VAZIO });
    const [atividades, setAtividades] = useState([]);
    const [novaAtividade, setNovaAtividade] = useState("");
    const [tentouEnviar, setTentouEnviar] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [salvoIncompleto, setSalvoIncompleto] = useState(false);

    useEffect(() => {
        setCnpj(ficha?.cnpj || "");
        setRazaoSocial(ficha?.razaoSocial || "");
        setNomeFantasia(ficha?.nomeFantasia || "");
        setCapitalSocial(capitalParaTela(ficha?.capitalSocial));
        setEndereco(separarEndereco(ficha?.enderecoEmpresa));
        setAtividades(Array.isArray(ficha?.atividades) ? ficha.atividades : []);
    }, [ficha]);

    const validar = () => {
        const erros = {};
        const digitosCnpj = TirarMascara(cnpj);
        if (digitosCnpj && digitosCnpj.length !== 14) erros.cnpj = "O CNPJ deve ter 14 dígitos.";
        if (!razaoSocial.trim()) erros.razaoSocial = "Informe a razão social (ou a sugestão de nome).";
        if (!nomeFantasia.trim()) erros.nomeFantasia = "Informe o nome fantasia (ou a sugestão de nome).";
        if (!TirarMascara(capitalSocial)) erros.capitalSocial = "Informe o capital social.";
        if (!enderecoCompleto(endereco)) {
            ["cep", "rua", "numero", "bairro", "cidade", "estado"].forEach((c) => {
                if (!endereco[c]) erros[c] = "Obrigatório.";
            });
        }
        if (atividades.length === 0) erros.atividades = "Adicione pelo menos uma atividade.";
        return erros;
    };

    const erros = tentouEnviar ? validar() : {};
    const { cep, rua, numero, bairro, cidade, estado, ...errosEmpresa } = erros;
    const errosEndereco = { cep, rua, numero, bairro, cidade, estado };

    const adicionarAtividade = () => {
        const texto = novaAtividade.trim();
        if (!texto) return;
        if (!atividades.some((a) => a.toLowerCase() === texto.toLowerCase())) setAtividades([...atividades, texto]);
        setNovaAtividade("");
    };

    // Salva mesmo incompleto (o progresso mostra o que falta); só bloqueia CNPJ inválido
    const handleSalvar = async (avancar) => {
        setTentouEnviar(true);
        const errosAtuais = validar();
        if (errosAtuais.cnpj) return;

        setSalvando(true);
        const dados = {
            cnpj: TirarMascara(cnpj) || null,
            razaoSocial,
            nomeFantasia,
            capitalSocial: capitalParaBanco(capitalSocial),
            enderecoEmpresa: montarEndereco(endereco) || null,
            atividades,
        };
        const config = { headers: { Authorization: localStorage.getItem("token") } };

        try {
            const response = ficha
                ? await axios.put(`${process.env.REACT_APP_API_URL}/atualizarFichaCadastral`, dados, config)
                : await axios.post(`${process.env.REACT_APP_API_URL}/adicionarFichaCadastral`, dados, config);

            if (response.data.status === 200) {
                setSalvoIncompleto(Object.keys(errosAtuais).length > 0);
                await onSalvo(ficha ? "Dados da empresa atualizados!" : "Ficha cadastral criada! Agora adicione os sócios.");
                if (avancar) onAvancar();
            } else {
                onErro(response.data.mensagem || "Erro ao salvar os dados da empresa.");
            }
        } catch (error) {
            onErro(error.response?.data?.mensagem || "Erro ao salvar os dados da empresa.");
        } finally {
            setSalvando(false);
        }
    };

    const campo = (id, label, valor, setValor, props = {}) => (
        <Box>
            <Typography component="label" htmlFor={id} sx={labelSx}>{label}</Typography>
            <TextField
                id={id}
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                size="small"
                fullWidth
                disabled={salvando}
                sx={inputSx}
                {...props}
            />
        </Box>
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={cardSx}>
                <SecaoTitulo icone={<ApartmentOutlinedIcon />} titulo="Dados da empresa" subtitulo="Se a empresa ainda não foi aberta, informe as sugestões de nome." />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={gradeSx(2)}>
                        {campo("ficha-cnpj", "CNPJ (se já tiver)", MascaraCnpj(cnpj), setCnpj, {
                            placeholder: "00.000.000/0000-00",
                            error: Boolean(errosEmpresa.cnpj),
                            helperText: errosEmpresa.cnpj || "Deixe em branco se a empresa ainda será aberta.",
                        })}
                        {campo("ficha-capital", "Capital social", capitalSocial, (v) => setCapitalSocial(MascaraValor(v)), {
                            placeholder: "0,00",
                            error: Boolean(errosEmpresa.capitalSocial),
                            helperText: errosEmpresa.capitalSocial || "Valor investido pelos sócios na empresa.",
                            slotProps: { input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } },
                        })}
                    </Box>
                    <Box sx={gradeSx(2)}>
                        {campo("ficha-razao", "Razão social", razaoSocial, setRazaoSocial, {
                            placeholder: "Ex.: Clínica Silva Serviços Médicos Ltda",
                            error: Boolean(errosEmpresa.razaoSocial),
                            helperText: errosEmpresa.razaoSocial || "Nome jurídico, usado em contratos e notas fiscais.",
                        })}
                        {campo("ficha-fantasia", "Nome fantasia", nomeFantasia, setNomeFantasia, {
                            placeholder: "Ex.: Clínica Silva",
                            error: Boolean(errosEmpresa.nomeFantasia),
                            helperText: errosEmpresa.nomeFantasia || "Nome pelo qual a empresa é conhecida pelo público.",
                        })}
                    </Box>
                </Box>
            </Box>

            <Box sx={cardSx}>
                <SecaoTitulo icone={<PlaceOutlinedIcon />} titulo="Endereço da empresa" subtitulo="Endereço registrado (ou que será registrado) nos órgãos públicos." />
                <CamposEndereco idPrefixo="ficha-endereco" endereco={endereco} onChange={setEndereco} disabled={salvando} erros={errosEndereco} />
            </Box>

            <Box sx={cardSx}>
                <SecaoTitulo icone={<WorkOutlineIcon />} titulo="Atividades da empresa" subtitulo="Descreva os serviços que a empresa vai prestar." />
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <TextField
                        id="ficha-atividade"
                        placeholder="Ex.: Consultas médicas em clínica"
                        value={novaAtividade}
                        onChange={(e) => setNovaAtividade(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                adicionarAtividade();
                            }
                        }}
                        size="small"
                        fullWidth
                        disabled={salvando}
                        error={Boolean(errosEmpresa.atividades)}
                        helperText={errosEmpresa.atividades || "Pressione Enter ou clique em adicionar."}
                        sx={inputSx}
                    />
                    <IconButton aria-label="Adicionar atividade" onClick={adicionarAtividade} disabled={salvando || !novaAtividade.trim()} sx={{ backgroundColor: "#f3f4f6", borderRadius: "8px", "&:hover": { backgroundColor: "#e5e7eb" } }}>
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Box>
                {atividades.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {atividades.map((atividade) => (
                            <Chip
                                key={atividade}
                                label={atividade}
                                onDelete={salvando ? undefined : () => setAtividades(atividades.filter((a) => a !== atividade))}
                                sx={{ backgroundColor: "#f3f4f6", color: "#111827", fontSize: 13, maxWidth: "100%" }}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {salvoIncompleto && Object.keys(erros).length > 0 && (
                <Alert severity="warning" sx={{ borderRadius: "8px", fontSize: 13 }}>
                    Dados salvos parcialmente. Complete os campos destacados para concluir a ficha.
                </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1 }}>
                <Button startIcon={<SaveOutlinedIcon />} onClick={() => handleSalvar(false)} disabled={salvando} sx={botaoSecundarioSx}>
                    Salvar
                </Button>
                <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => handleSalvar(true)} loading={salvando} sx={botaoPrincipalSx}>
                    Salvar e continuar para sócios
                </Button>
            </Box>
        </Box>
    );
};

export default DadosEmpresaFicha;
